import dayjs from 'dayjs';
import {injectable} from 'inversify';

import type {
  DateObj,
  DateTimeObj,
  NaiveDateTimeObj,
} from '../../../utils/datetime';

import {
  dateObjToDateString,
  dateTimeObjToTimestamp,
  naiveDateTimeObjToTimestamp,
} from '../../../utils/datetime';
import {DateTimeModel} from './DateTime.model';

@injectable()
export class DateTimeModelFactory {
  create(value: DateTimeModel.Value, isUtc = false) {
    return new DateTimeModel({isUtc, value});
  }

  createFromDateObject(obj: DateObj, isUtc = false) {
    const value = dayjs(dateObjToDateString(obj), {utc: isUtc}).valueOf();

    return this.create(value, isUtc);
  }

  createFromDateTimeObject(
    obj: DateTimeModelFactory.CreateFromDateProps,
    isUtc = false,
  ) {
    const value = dateTimeObjToTimestamp(obj);

    return this.create(value, isUtc);
  }

  createFromNaiveDateTimeObject(
    obj: DateTimeModelFactory.CreateFromNaiveDateProps,
    isUtc = false,
  ) {
    const value = naiveDateTimeObjToTimestamp(obj);

    return this.create(value, isUtc);
  }

  createFromString(value: string, isUtc = false) {
    const nextDate = dayjs(value, {utc: isUtc});

    if (nextDate.isValid()) {
      return this.create(nextDate.valueOf(), isUtc);
    } else {
      throw new Error('Invalid date value property');
    }
  }

  generateRange({end, start, unit}: DateTimeModelFactory.RangeGeneratorProps) {
    const result: DateTimeModel[] = [];

    let current = start;

    while (current.lte(end, unit)) {
      result.push(current);
      current = current.add(1, unit);
    }

    return result;
  }

  guessTimezone() {
    return dayjs.tz.guess();
  }

  isValid(date: string, format: string) {
    return dayjs(date, format, true).isValid();
  }

  now(isUtc = false) {
    return this.create(Date.now(), isUtc);
  }
}

export namespace DateTimeModelFactory {
  export type CreateFromDateProps = DateObj | DateTimeObj | NaiveDateTimeObj;
  export type CreateFromNaiveDateProps = NaiveDateTimeObj;

  export type RangeGeneratorProps = {
    end: DateTimeModel;
    start: DateTimeModel;
    unit: Extract<
      DateTimeModel.TimeUnit,
      'day' | 'hour' | 'month' | 'week' | 'year'
    >;
  };
}
